import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '@app/_services/auth/auth.service';
import { UserAuthDto } from '@app/shared/model/user.model';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { HttpResponse } from '@angular/common/http';
import { UserLicence } from '@app/shared/model/userLicence.model';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';
import { ToastrService } from 'ngx-toastr';

Chart.register(...registerables);

interface UserDto {
  id: number;
  username: string;
  fullName: string;
  emailAddress: string;
  password: string;
  isActive: boolean;
}

@Component({
  selector: 'app-auth-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-dashboard.component.html',
  styleUrl: './auth-dashboard.component.scss'
})
export class AuthDashboardComponent implements OnInit {
  @ViewChild('licenseChart', { static: false }) licenseChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('userChart', { static: false }) userChartRef!: ElementRef<HTMLCanvasElement>;

  currentUser: UserAuthDto | null = null;
  licenseChart: Chart | null = null;
  userChart: Chart | null = null;

  // Data properties
  totalLicenses: number = 0;
  demoLicenses: number = 0;
  standardLicenses: number = 0;
  activeUsers: number = 0;
  inactiveUsers: number = 0;
  totalUsers: number = 0;

  isLoading: boolean = true;

  constructor(
    private authService: AuthService,
    private apiRequestService: ApiRequestService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUserValue();
    console.log('Dashboard loaded. Current user:', this.currentUser);
    this.loadDashboardData();
  }

  loadDashboardData(): void {
    this.isLoading = true;
    
    // Load licenses
    this.apiRequestService.getRequest<UserLicence[]>('License')
      .subscribe({
        next: (response: HttpResponse<UserLicence[]>) => {
          if (response.body) {
            this.processLicenseData(response.body);
            this.createLicenseChart();
          }
        },
        error: (error) => {
          console.error('Failed to load licenses:', error);
          this.toastrService.error('Failed to load license data', 'Error');
        }
      });

    // Load active users
    this.apiRequestService.getRequest<UserDto[]>('User?IsActive=true')
      .subscribe({
        next: (response: HttpResponse<UserDto[]>) => {
          if (response.body) {
            this.activeUsers = response.body.length;
            this.checkAndCreateUserChart();
          }
        },
        error: (error) => {
          console.error('Failed to load active users:', error);
        }
      });

    // Load inactive users
    this.apiRequestService.getRequest<UserDto[]>('User?IsActive=false')
      .subscribe({
        next: (response: HttpResponse<UserDto[]>) => {
          if (response.body) {
            this.inactiveUsers = response.body.length;
            this.checkAndCreateUserChart();
          }
        },
        error: (error) => {
          console.error('Failed to load inactive users:', error);
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  processLicenseData(licenses: UserLicence[]): void {
    this.totalLicenses = licenses.length;
    this.demoLicenses = licenses.filter(l => l.licenseType === 1).length; // 1 = Demo
    this.standardLicenses = licenses.filter(l => l.licenseType === 2).length; // 2 = Standard
  }

  checkAndCreateUserChart(): void {
    // Create chart only when both active and inactive data are loaded
    if (this.activeUsers >= 0 && this.inactiveUsers >= 0 && !this.userChart) {
      this.totalUsers = this.activeUsers + this.inactiveUsers;
      setTimeout(() => this.createUserChart(), 100);
    }
  }

  createLicenseChart(): void {
    setTimeout(() => {
      if (!this.licenseChartRef) return;

      const ctx = this.licenseChartRef.nativeElement.getContext('2d');
      if (!ctx) return;

      // Destroy existing chart if it exists
      if (this.licenseChart) {
        this.licenseChart.destroy();
      }

      this.licenseChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: ['Demo Licenses', 'Standard Licenses'],
          datasets: [{
            data: [this.demoLicenses, this.standardLicenses],
            backgroundColor: [
              'rgba(255, 159, 64, 0.8)',
              'rgba(54, 162, 235, 0.8)'
            ],
            borderColor: [
              'rgba(255, 159, 64, 1)',
              'rgba(54, 162, 235, 1)'
            ],
            borderWidth: 2,
            hoverOffset: 15
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                padding: 15,
                font: {
                  size: 13
                },
                usePointStyle: true
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 14,
                weight: 'bold'
              },
              bodyFont: {
                size: 13
              },
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  const total = this.totalLicenses;
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  return `${label}: ${value} (${percentage}%)`;
                }
              }
            }
          },
          animation: {
            animateRotate: true,
            animateScale: true
          }
        }
      });
    }, 100);
  }

  createUserChart(): void {
    if (!this.userChartRef) return;

    const ctx = this.userChartRef.nativeElement.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart if it exists
    if (this.userChart) {
      this.userChart.destroy();
    }

    this.userChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Active Users', 'Inactive Users'],
        datasets: [{
          label: 'User Count',
          data: [this.activeUsers, this.inactiveUsers],
          backgroundColor: [
            'rgba(75, 192, 192, 0.8)',
            'rgba(255, 99, 132, 0.8)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(255, 99, 132, 1)'
          ],
          borderWidth: 2,
          borderRadius: 8,
          barThickness: 60
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
              font: {
                size: 12
              }
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 13
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 13
            },
            callbacks: {
              label: (context) => {
                const value = context.parsed.y || 0;
                const total = this.totalUsers;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return `Count: ${value} (${percentage}%)`;
              }
            }
          }
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.licenseChart) {
      this.licenseChart.destroy();
    }
    if (this.userChart) {
      this.userChart.destroy();
    }
  }
}
